import { Middleware } from '@reduxjs/toolkit';

import { getCookie, QaToolKitOptions } from '@cashier/cashier-common';
import {
  confirmPaymentFailure,
  confirmPaymentSuccess,
  fetchPaymentCurrentState,
  fetchPaymentCurrentStateSuccess,
  fetchPaymentInformationSuccess,
  processPaymentFailure,
  processPaymentSuccess,
  resetPaymentMethod,
  selectPaymentFormPayload,
  selectPaymentMethod,
  selectProcessingResult,
  setActiveScreen,
} from '@cashier/cashier-data-access';
import {
  CashierMessages,
  DynamicScreens,
  PaymentActionResult,
  PaymentAttribute,
  PaymentStates,
  PreprocessorTypes,
  RedirectContainers,
} from '@cashier/cashier-model';
import {
  eventBus,
  EventFactory,
  PaymentStateChecker,
  ProviderWindowService,
  SdkProcessorService,
} from '@cashier/cashier-shared';

export const commonMiddleware: Middleware =
  (store) => (next) => async (action) => {
    const result = next(action);
    const {
      cashierConfiguration: { notifyCustomerAboutPaymentResult, paymentType },
    } = store.getState();

    if (action.type === fetchPaymentInformationSuccess.toString()) {
      const { payload }: { payload: PaymentAttribute } = action;

      if (payload.state === PaymentStates.PROCESSING) {
        store.dispatch(fetchPaymentCurrentState(payload.transactionId));
      }
    }

    if (action.type === fetchPaymentCurrentStateSuccess.toString()) {
      const { payload }: { payload: PaymentAttribute } = action;
      const {
        customerInformation: { activeScreen },
      } = store.getState();

      if (activeScreen !== DynamicScreens.PaymentForm) {
        if (payload.state === PaymentStates.PROCESSING) {
          store.dispatch(fetchPaymentCurrentState(payload.transactionId));
        }

        if (
          PaymentStateChecker.isFinalized(paymentType, payload.state) &&
          !notifyCustomerAboutPaymentResult
        ) {
          eventBus.emit(
            EventFactory.paymentProcessed(
              [
                PaymentStates.FORCE_SUCCESSFUL,
                PaymentStates.SUCCESSFUL,
              ].includes(payload.state)
                ? 200
                : 500,
              {},
            ),
          );
        }
      }
    }

    /* resetAmount after any changes in PaymentMethod */
    if (action.type === resetPaymentMethod.toString()) {
      store.dispatch(setActiveScreen(DynamicScreens.PaymentForm));
    }

    //proxy for processPaymentSuccess
    if (action.type === confirmPaymentSuccess.toString()) {
      const { payload }: { payload: PaymentActionResult } = action;
      const paymentMethod = selectPaymentMethod(store.getState());

      // TODO delete after testing 2914
      const isHostedFieldEnabled =
        getCookie(QaToolKitOptions.enableHostedField) === 'true';

      if (
        paymentMethod.preprocessor?.type === PreprocessorTypes.SDK_PROCESSOR_V1
      ) {
        if (isHostedFieldEnabled) {
          const processingResult = selectProcessingResult(store.getState());

          eventBus.emit(
            EventFactory.cashierMessage({
              task: CashierMessages.PostProcessSdkProcessorPayment,
              paymentAttributes: processingResult.paymentAttributes,
            }),
          );

          return;
        } else {
          try {
            const formPayload = selectPaymentFormPayload(store.getState());
            const processingResult = selectProcessingResult(store.getState());

            const response = await SdkProcessorService.processPayment(
              processingResult.paymentAttributes,
              formPayload,
              paymentMethod.preprocessor,
            );

            const isRedirect =
              response.type === 'redirect' &&
              response.redirectUrl &&
              RedirectContainers.NEW_TAB === paymentMethod.redirectContainer;

            if (isRedirect) {
              ProviderWindowService.redirect(
                response.redirectUrl,
                paymentMethod.redirectContainer === RedirectContainers.NEW_TAB,
              );

              return;
            }

            store.dispatch(processPaymentSuccess(payload));
          } catch (error) {
            const transactionId =
              error && typeof error === 'object' && 'transactionId' in error
                ? (error.transactionId as string) || null
                : null;
            store.dispatch(
              processPaymentFailure({
                message: JSON.stringify(error),
                attributeErrorBag: null,
                errorDetails: { description: JSON.stringify(error) },
                transactionId,
                context: null,
              }),
            );
          }

          return;
        }
      }

      store.dispatch(processPaymentSuccess(payload));
    }

    if (action.type === confirmPaymentFailure.toString()) {
      store.dispatch(processPaymentFailure(action.payload));
    }

    return result;
  };
